import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH ALL FOLLOWUPS
export const fetchAllFollowups = createAsyncThunk(
  "followups/fetchAll",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/followups", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch followups",
      );
    }
  },
);

// FETCH FOLLOWUP STATS
export const fetchFollowupStats = createAsyncThunk(
  "followups/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/followups/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch stats"
      );
    }
  }
);

// BATCH RESCHEDULE FOLLOWUPS
export const batchRescheduleFollowups = createAsyncThunk(
  "followups/batchReschedule",
  async ({ days = 1, ids = [] } = {}, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/followups/batch-reschedule", { days, ids });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to batch reschedule followups"
      );
    }
  }
);

// BATCH COMPLETE FOLLOWUPS
export const batchCompleteFollowups = createAsyncThunk(
  "followups/batchComplete",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/followups/batch-complete", payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to batch complete followups"
      );
    }
  }
);

// GET FOLLOWUP BY ID
export const getFollowupById = createAsyncThunk(
  "followups/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/followups/${id}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch followup",
      );
    }
  },
);

// CREATE FOLLOWUP
export const createFollowup = createAsyncThunk(
  "followups/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/followups", payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to create followup",
      );
    }
  },
);

// UPDATE FOLLOWUP
export const updateFollowup = createAsyncThunk(
  "followups/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/followups/${id}`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to update followup",
      );
    }
  },
);

// DELETE FOLLOWUP
export const deleteFollowup = createAsyncThunk(
  "followups/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/tenant/followups/${id}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete followup",
      );
    }
  },
);

// BULK DELETE FOLLOWUPS
export const bulkDeleteFollowups = createAsyncThunk(
  "followups/bulkDelete",
  async (ids, { rejectWithValue }) => {
    try {
      const res = await Axios.delete("/tenant/followups/bulk-delete", {
        data: { ids },
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete followups",
      );
    }
  },
);

// CHANGE FOLLOWUP COMPLETION STATUS
export const changeFollowupCompletionStatus = createAsyncThunk(
  "followups/changeCompletionStatus",
  async (input, { rejectWithValue }) => {
    try {
      const id = typeof input === "object" ? input?.id : input;
      const payload =
        typeof input === "object" && input?.payload ? input.payload : {};
      const res = await Axios.post(
        `/tenant/followups/${id}/change-completion-status`,
        payload,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to change status",
      );
    }
  },
);

// GET FOLLOWUP HISTORY
export const getFollowupHistory = createAsyncThunk(
  "followups/getHistory",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/followup-histories`, { params });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch history");
    }
  },
);

// FETCH FOLLOWUP CONFIGURATION DATA
export const fetchFollowupConfigurationData = createAsyncThunk(
  "followups/fetchConfiguration",
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/followups/configurations");
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch configurations"
      );
    }
  }
);

// PATCH UPDATE FOLLOWUP
export const patchUpdateFollowup = createAsyncThunk(
  "followups/patchUpdate",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.patch(`/tenant/followups/${id}`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update followup");
    }
  },
);

// FETCH FOLLOWUP NOTES
export const fetchFollowupNotes = createAsyncThunk(
  "followups/fetchNotes",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/followup-notes", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch notes");
    }
  },
);

// ADD FOLLOWUP NOTE
export const addFollowupNote = createAsyncThunk(
  "followups/addNote",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/followup-notes", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add note");
    }
  },
);

// UPDATE FOLLOWUP NOTE
export const updateFollowupNote = createAsyncThunk(
  "followups/updateNote",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/followup-notes/${id}`, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update note");
    }
  },
);

// DELETE FOLLOWUP NOTE
export const deleteFollowupNote = createAsyncThunk(
  "followups/deleteNote",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/tenant/followup-notes/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to delete note");
    }
  },
);

const followupSlice = createSlice({
  name: "followups",
  initialState: {
    followups: [],
    selectedFollowup: null,
    total_records: 0,
    isFollowupsLoading: false,
    isLoading: false,
    isConfigLoading: false,
    error: null,
    isBulkDeleting: false,
    followupHistories: [],
    stats: {
      total_count: 0,
      active_count: 0,
      overdue_count: 0,
      today_count: 0,
      upcoming_count: 0,
      completed_count: 0,
      cancelled_count: 0,
      completion_rate: "0%"
    },
    isStatsLoading: false,
    configurationData: {
      assigned_to: [],
      followup_statuses: [],
      followup_types: [],
    },
    currentRequestId: null,
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchAllFollowups.pending, (state, action) => {
        state.isFollowupsLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAllFollowups.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isFollowupsLoading = false;
        state.followups = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllFollowups.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isFollowupsLoading = false;
        state.error = action.payload || "Failed to fetch followups";
      })

      // Configuration Data
      .addCase(fetchFollowupConfigurationData.pending, (state) => {
        state.isConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchFollowupConfigurationData.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.configurationData = action.payload;
      })
      .addCase(fetchFollowupConfigurationData.rejected, (state, action) => {
        state.isConfigLoading = false;
        state.error = action.payload || "Failed to fetch configurations";
      })

      // FETCH FOLLOWUP STATS
      .addCase(fetchFollowupStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchFollowupStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchFollowupStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      // BATCH RESCHEDULE
      .addCase(batchRescheduleFollowups.fulfilled, (state) => {
        state.isLoading = false;
      })

      // BATCH COMPLETE
      .addCase(batchCompleteFollowups.fulfilled, (state) => {
        state.isLoading = false;
      })

// GET FOLLOWUP BY ID
      .addCase(getFollowupById.pending, (state) => {
        state.isLoading = true;
        state.selectedFollowup = null;
        state.error = null;
      })

      .addCase(getFollowupById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedFollowup = action.payload;
      })

      .addCase(getFollowupById.rejected, (state, action) => {
        state.isLoading = false;
        state.selectedFollowup = null;
        state.error = action.payload || "Failed to fetch followup";
      })

      // GET FOLLOWUP HISTORY
      .addCase(getFollowupHistory.pending, (state) => {
        state.isFollowupsLoading = true;
        state.followupHistories = [];
      })
      .addCase(getFollowupHistory.fulfilled, (state, action) => {
        state.isFollowupsLoading = false;
        state.followupHistories = action.payload;
      })
      .addCase(getFollowupHistory.rejected, (state, action) => {
        state.isFollowupsLoading = false;
        state.error = action.payload;
      });
  },
});

export default followupSlice.reducer;
